/**
 * Settings → Verse of the week. Prop-free; the integrator mounts <VerseSettingsPanel />.
 *
 * Three jobs, in the order Brian will do them:
 *   1. Pick a translation — from the list his key is ACTUALLY allowed to read. Nothing is
 *      hardcoded, and a Bible ID the key can't read is rejected by the Worker, not stored
 *      and then quietly failing at 6am every Monday.
 *   2. Refresh now — so the first verse lands today instead of on Monday.
 *   3. Edit the reading plan — the plan is config: add, reorder, retire. The cron just
 *      walks whatever is active, and wraps at the end.
 *
 * Adults-only work, enforced in the Worker. This panel still handles a 403 honestly rather
 * than assuming it will never see one.
 */
import { useId, useState } from 'react';
import type { BibleTranslation, VerseConfig, VerseOfWeek, VersePlanEntry } from '../../shared/types';
import { useIsAdult } from '../../lib/identity';
import { ArrowIcon, Button, IconButton, Note, Select, Skeleton, TextInput, useAsync } from './ui';
import {
  addEntry,
  editEntry,
  explain,
  getConfig,
  getPlan,
  getTranslations,
  getVerse,
  postRefresh,
  prettyDate,
  putConfig,
  removeEntry,
  reorderPlan,
  thisMonday,
} from './verseApi';

export default function VerseSettingsPanel() {
  const adult = useIsAdult();

  const config = useAsync<VerseConfig>('verse-config', getConfig);
  const verse = useAsync<VerseOfWeek | null>('verse', getVerse);
  const plan = useAsync<VersePlanEntry[]>('verse-plan', getPlan, adult);

  const configured = config.data?.configured ?? false;
  const translations = useAsync<BibleTranslation[]>(
    'verse-translations',
    getTranslations,
    adult && configured
  );

  return (
    <section className="space-y-6 rounded-card border border-border bg-surface p-5">
      <header>
        <h2 className="font-display text-xl font-semibold text-ink">Verse of the week</h2>
        {/* No length claimed here: the plan is editable config, and this header renders
            before the plan has loaded. PlanSection counts what it actually has. */}
        <p className="mt-1 text-sm text-ink-soft">
          A verse lands on everyone&rsquo;s home screen every Monday morning. The plan below is
          what it walks, in order — and when it reaches the end it starts over.
        </p>
      </header>

      {!adult && (
        <Note tone="warn">Only a grown-up can change the translation or the reading plan.</Note>
      )}

      {config.loading && <Skeleton className="h-11 w-full" />}
      {!config.loading && config.error !== null && (
        <div className="space-y-3">
          <Note tone="warn" role="alert">
            {explain(config.error, "Couldn't load the verse settings.")}
          </Note>
          <Button onClick={config.reload}>Try again</Button>
        </div>
      )}

      {!config.loading && config.error === null && config.data && (
        <>
          <TranslationSection
            adult={adult}
            config={config.data}
            translations={translations}
            onSaved={(next) => {
              config.set(next);
              verse.reload();
            }}
          />

          <ThisWeekSection
            adult={adult}
            config={config.data}
            verse={verse}
            onRefreshed={() => config.reload()}
          />

          {adult && <PlanSection plan={plan} currentPlanId={verse.data?.plan_id ?? null} />}
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- translation

function TranslationSection({
  adult,
  config,
  translations,
  onSaved,
}: {
  adult: boolean;
  config: VerseConfig;
  translations: ReturnType<typeof useAsync<BibleTranslation[]>>;
  onSaved: (config: VerseConfig) => void;
}) {
  const selectId = useId();
  const [choice, setChoice] = useState<string>(config.bible_id ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The config the Worker actually returned from the save — not a boolean "it 200'd".
  // PUT /api/verse/config saves the translation and THEN re-fetches this week's verse; the
  // re-fetch can fail while the save succeeds, and the only place that says so is
  // `last_error` on the body it hands back. Reading it is the difference between the truth
  // and a fabricated all-clear.
  const [savedConfig, setSavedConfig] = useState<VerseConfig | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    setSavedConfig(null);
    try {
      const next = await putConfig(choice);
      onSaved(next);
      setSavedConfig(next);
    } catch (err) {
      setError(explain(err, 'That translation did not save.'));
    } finally {
      setSaving(false);
    }
  }

  // No key on the Worker. This is a real, nameable blocker — say exactly what fixes it.
  if (!config.configured) {
    return (
      <div className="space-y-3 border-t border-border pt-5">
        <h3 className="text-sm font-semibold text-ink">Translation</h3>
        <Note tone="warn">
          api.bible isn&rsquo;t connected. The Worker needs a <code>BIBLE_API_KEY</code> secret
          before a translation can be picked.
        </Note>
      </div>
    );
  }

  return (
    <div className="space-y-3 border-t border-border pt-5">
      <h3 className="text-sm font-semibold text-ink">Translation</h3>

      {translations.loading && <Skeleton className="h-11 w-full" />}

      {!translations.loading && translations.error !== null && (
        <div className="space-y-3">
          <Note tone="warn" role="alert">
            {explain(translations.error, "Couldn't load the translations your key can read.")}
          </Note>
          <Button onClick={translations.reload}>Try again</Button>
        </div>
      )}

      {!translations.loading && translations.error === null && translations.data?.length === 0 && (
        <Note tone="warn">
          api.bible answered, but this key isn&rsquo;t approved for any translations yet.
        </Note>
      )}

      {!translations.loading &&
        translations.error === null &&
        translations.data &&
        translations.data.length > 0 && (
          <>
            <label htmlFor={selectId} className="block text-sm font-medium text-ink">
              Which translation should the family read?
            </label>
            <p className="text-xs text-ink-soft">
              These are the {translations.data.length} translations this key is allowed to read.
            </p>
            <Select
              id={selectId}
              value={choice}
              disabled={!adult || saving}
              onChange={(e) => {
                setChoice(e.target.value);
                setSavedConfig(null);
              }}
            >
              <option value="">Pick a translation…</option>
              {translations.data.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.abbreviation ? `${t.abbreviation} — ` : ''}
                  {t.name}
                  {t.language ? ` (${t.language})` : ''}
                </option>
              ))}
            </Select>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="primary"
                onClick={save}
                disabled={!adult || saving || !choice || choice === config.bible_id}
              >
                {saving ? 'Saving…' : 'Save translation'}
              </Button>
              {config.translation && (
                <span className="text-sm text-ink-soft">
                  Currently reading <strong className="font-medium text-ink">{config.translation}</strong>
                </span>
              )}
            </div>

            {error && (
              <Note tone="warn" role="alert">
                {error}
              </Note>
            )}

            {/* Half the write can land: the translation is stored, the re-fetch is not.
                Say exactly which half, and give the reason api.bible gave us. */}
            {savedConfig && !error && savedConfig.last_error && (
              <Note tone="warn" role="alert">
                Saved the translation
                {savedConfig.translation ? ` (${savedConfig.translation})` : ''}, but this
                week&rsquo;s verse could not be re-fetched in it, so the verse below is still in
                the old one. {savedConfig.last_error} Try &ldquo;Refresh now&rdquo; once that&rsquo;s
                sorted.
              </Note>
            )}
            {savedConfig && !error && !savedConfig.last_error && (
              <Note tone="good" role="status">
                Saved — this week&rsquo;s verse was re-fetched in
                {savedConfig.translation ? ` ${savedConfig.translation}` : ' the new translation'}.
              </Note>
            )}
          </>
        )}
    </div>
  );
}

// ---------------------------------------------------------------- this week

function ThisWeekSection({
  adult,
  config,
  verse,
  onRefreshed,
}: {
  adult: boolean;
  config: VerseConfig;
  verse: ReturnType<typeof useAsync<VerseOfWeek | null>>;
  onRefreshed: () => void;
}) {
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setRefreshing(true);
    setError(null);
    try {
      verse.set(await postRefresh());
      onRefreshed();
    } catch (err) {
      setError(explain(err, 'The refresh did not go through.'));
    } finally {
      setRefreshing(false);
    }
  }

  const current = verse.data;
  const stale = current !== null && current.week_start < thisMonday();

  return (
    <div className="space-y-3 border-t border-border pt-5">
      <h3 className="text-sm font-semibold text-ink">This week</h3>

      {verse.loading && <Skeleton className="h-5 w-2/3" />}

      {!verse.loading && verse.error !== null && (
        <Note tone="warn" role="alert">
          {explain(verse.error, "Couldn't load the cached verse.")}
        </Note>
      )}

      {!verse.loading && verse.error === null && (
        <p className="text-sm leading-relaxed text-ink-soft">
          {current ? (
            <>
              <strong className="font-medium text-ink">{current.reference}</strong>
              {current.translation ? ` (${current.translation})` : ''} — week of{' '}
              {prettyDate(current.week_start)}.
              {stale && <> This week&rsquo;s hasn&rsquo;t landed yet.</>}
            </>
          ) : config.bible_id ? (
            'Nothing fetched yet. The first verse lands Monday — or bring it in now.'
          ) : (
            'Pick a translation above, and the plan starts.'
          )}
        </p>
      )}

      {adult && (
        <Button variant={stale || !current ? 'primary' : 'secondary'} onClick={refresh} disabled={refreshing}>
          {refreshing ? 'Fetching…' : 'Refresh now'}
        </Button>
      )}

      {error && (
        <Note tone="warn" role="alert">
          {error}
        </Note>
      )}
      {/* The last thing that went wrong, straight from the Worker. Not a guess at a cause. */}
      {!error && config.last_error && <Note tone="warn">{config.last_error}</Note>}
    </div>
  );
}

// ---------------------------------------------------------------- reading plan

function PlanSection({
  plan,
  currentPlanId,
}: {
  plan: ReturnType<typeof useAsync<VersePlanEntry[]>>;
  currentPlanId: number | null;
}) {
  const [moveError, setMoveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const entries = plan.data ?? [];
  const loaded = !plan.loading && plan.error === null;

  // A full plan is long enough to turn Settings into a several-thousand-pixel scroll on the
  // phone this actually lives on, so the list starts at the top of the plan and opens on
  // request. Slicing from the front keeps every index aligned with the full list, so
  // reordering stays correct.
  const PREVIEW = 8;
  const visible = showAll ? entries : entries.slice(0, PREVIEW);
  const hidden = entries.length - visible.length;

  /**
   * Runs one plan mutation. Returns null on success, or the reason it failed — the CALLER
   * decides what to do with that, because the caller is the one holding text the family
   * typed. A write that didn't land must never look like one that did, and must never take
   * their words down with it.
   */
  async function run(work: () => Promise<VersePlanEntry[] | void>): Promise<string | null> {
    setBusy(true);
    try {
      const next = await work();
      if (next) plan.set(next);
      else plan.reload();
      return null;
    } catch (err) {
      // Deliberately NO reload here.
      //
      // The write was REJECTED, so the server's list is unchanged and what's on screen is
      // still accurate — there is nothing to re-sync. Reloading here used to destroy the
      // family's work: the reload flipped plan.loading, the <ol> is gated on !plan.loading,
      // the editing row unmounted, and React threw away the text they had typed. They got
      // no error and no words back.
      //
      // A failed write must leave their text exactly where it is, with the reason next to it.
      return explain(err, 'That change did not save.');
    } finally {
      setBusy(false);
    }
  }

  const move = async (index: number, delta: number) => {
    const next = [...entries];
    const [row] = next.splice(index, 1);
    next.splice(index + delta, 0, row);
    setMoveError(await run(() => reorderPlan(next.map((e) => e.id))));
  };

  return (
    <div className="space-y-4 border-t border-border pt-5">
      <div>
        <h3 className="text-sm font-semibold text-ink">Reading plan</h3>
        {/* The count comes from the plan we're holding — never a hardcoded 52. Retire an
            entry and this number moves with it. */}
        <p className="mt-1 text-xs text-ink-soft">
          {loaded && entries.length > 0
            ? `${entries.length} ${entries.length === 1 ? 'verse' : 'verses'}, one a week, in this order. At the end it starts over from the top.`
            : 'One entry per week, in this order. At the end it starts over from the top.'}
        </p>
      </div>

      {plan.loading && (
        <div className="space-y-2" role="status" aria-live="polite">
          <span className="sr-only">Loading the reading plan…</span>
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-11 w-full" />
        </div>
      )}

      {!plan.loading && plan.error !== null && (
        <div className="space-y-3">
          <Note tone="warn" role="alert">
            {explain(plan.error, "Couldn't load the reading plan.")}
          </Note>
          <Button onClick={plan.reload}>Try again</Button>
        </div>
      )}

      {!plan.loading && plan.error === null && entries.length === 0 && (
        <Note tone="info">
          The plan is empty. Add a reference below and it goes up on the home screen next Monday.
        </Note>
      )}

      {moveError && (
        <Note tone="warn" role="alert">
          {moveError}
        </Note>
      )}

      {/* Stale-while-revalidate: keep the list MOUNTED during a background refresh.
          Gating this on !plan.loading unmounted every row whenever a reload ran, which
          silently destroyed any row state — including text mid-edit. Showing slightly
          stale rows for a moment is strictly better than deleting someone's work. */}
      {plan.error === null && entries.length > 0 && (
        <>
          <ol className="divide-y divide-border rounded-xl border border-border">
            {visible.map((entry, i) => (
              <PlanRow
                key={entry.id}
                entry={entry}
                index={i}
                total={entries.length}
                busy={busy}
                isThisWeek={entry.id === currentPlanId}
                onMoveUp={() => move(i, -1)}
                onMoveDown={() => move(i, 1)}
                onSave={(reference, note) =>
                  run(async () => void (await editEntry(entry.id, reference, note)))
                }
                onRemove={() => run(async () => void (await removeEntry(entry.id)))}
              />
            ))}
          </ol>

          {hidden > 0 && (
            <Button onClick={() => setShowAll(true)}>Show all {entries.length} verses</Button>
          )}
          {showAll && entries.length > PREVIEW && (
            <Button variant="ghost" onClick={() => setShowAll(false)}>
              Show fewer
            </Button>
          )}
        </>
      )}

      <AddEntryForm
        busy={busy}
        onAdd={(reference, note) => run(async () => void (await addEntry(reference, note)))}
      />
    </div>
  );
}

function PlanRow({
  entry,
  index,
  total,
  busy,
  isThisWeek,
  onMoveUp,
  onMoveDown,
  onSave,
  onRemove,
}: {
  entry: VersePlanEntry;
  index: number;
  total: number;
  busy: boolean;
  isThisWeek: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  /** Resolves to null on success, or the reason it failed. Never throws. */
  onSave: (reference: string, note: string | null) => Promise<string | null>;
  onRemove: () => Promise<string | null>;
}) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [reference, setReference] = useState(entry.reference);
  const [note, setNote] = useState(entry.note ?? '');
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const refId = useId();
  const noteId = useId();

  /** Wait for the write. Close the editor ONLY once the server has it — a rejected edit
   *  keeps the form open with every character still in it. */
  async function save() {
    setWorking(true);
    setError(null);
    const failure = await onSave(reference.trim(), note.trim() || null);
    setWorking(false);
    if (failure) {
      setError(failure);
      return;
    }
    setEditing(false);
  }

  async function remove() {
    setWorking(true);
    setError(null);
    const failure = await onRemove();
    setWorking(false);
    if (failure) {
      setError(failure);
      return;
    }
    setConfirming(false);
  }

  if (editing) {
    return (
      <li className="space-y-3 p-3">
        <div className="space-y-1.5">
          <label htmlFor={refId} className="text-sm font-medium text-ink">
            Reference
          </label>
          <TextInput
            id={refId}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Psalm 23:1-6"
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor={noteId} className="text-sm font-medium text-ink">
            Note <span className="font-normal text-ink-soft">(optional)</span>
          </label>
          <TextInput
            id={noteId}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Why this one"
          />
        </div>
        {/* The edit stayed on screen because it did not save. Say why, right here. */}
        {error && (
          <Note tone="warn" role="alert">
            {error}
          </Note>
        )}
        <div className="flex gap-2">
          <Button variant="primary" disabled={busy || working || !reference.trim()} onClick={save}>
            {working ? 'Saving…' : 'Save'}
          </Button>
          <Button
            variant="ghost"
            disabled={working}
            onClick={() => {
              setReference(entry.reference);
              setNote(entry.note ?? '');
              setError(null);
              setEditing(false);
            }}
          >
            Cancel
          </Button>
        </div>
      </li>
    );
  }

  return (
    <li className="p-2">
      <div className="flex items-center gap-1">
        <div className="flex shrink-0 flex-col">
          <IconButton
            label={`Move ${entry.reference} up`}
            disabled={busy || index === 0}
            onClick={onMoveUp}
            className="h-8 w-8"
          >
            <ArrowIcon dir="up" className="h-4 w-4" />
          </IconButton>
          <IconButton
            label={`Move ${entry.reference} down`}
            disabled={busy || index === total - 1}
            onClick={onMoveDown}
            className="h-8 w-8"
          >
            <ArrowIcon dir="down" className="h-4 w-4" />
          </IconButton>
        </div>

        <div className="min-w-0 flex-1 px-1">
          <p className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-ink">{entry.reference}</span>
            {isThisWeek && (
              <span className="inline-flex items-center rounded-full bg-sage-soft px-2 py-0.5 text-xs font-medium text-sage">
                ✓ This week
              </span>
            )}
          </p>
          {entry.note && <p className="mt-0.5 truncate text-xs text-ink-soft">{entry.note}</p>}
        </div>

        {confirming ? (
          <div className="flex shrink-0 items-center gap-1">
            <Button
              variant="ghost"
              disabled={busy || working}
              className="text-clay hover:bg-clay-soft hover:text-clay"
              onClick={remove}
            >
              {working ? 'Removing…' : 'Remove?'}
            </Button>
            <Button
              variant="ghost"
              disabled={working}
              onClick={() => {
                setError(null);
                setConfirming(false);
              }}
            >
              Keep
            </Button>
          </div>
        ) : (
          <div className="flex shrink-0 items-center">
            <Button variant="ghost" disabled={busy} onClick={() => setEditing(true)}>
              Edit
            </Button>
            <Button variant="ghost" disabled={busy} onClick={() => setConfirming(true)}>
              Remove
            </Button>
          </div>
        )}
      </div>

      {/* A remove that didn't land leaves the entry exactly where it was, and says why. */}
      {error && !editing && (
        <div className="px-1 pt-2">
          <Note tone="warn" role="alert">
            {error}
          </Note>
        </div>
      )}
    </li>
  );
}

function AddEntryForm({
  busy,
  onAdd,
}: {
  busy: boolean;
  /** Resolves to null on success, or the reason it failed. Never throws. */
  onAdd: (reference: string, note: string | null) => Promise<string | null>;
}) {
  const refId = useId();
  const noteId = useId();
  const hintId = useId();
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  /**
   * Wait for the POST before touching the fields. Clearing them first meant a rejected
   * write — offline, a 500, a reference the Worker judged too long — wiped what they had
   * just typed and made them find the words again. The fields are cleared only once the
   * entry is actually in the plan.
   */
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const ref = reference.trim();
    if (!ref || adding) return;

    setAdding(true);
    setError(null);
    const failure = await onAdd(ref, note.trim() || null);
    setAdding(false);

    if (failure) {
      setError(failure); // their words stay on screen, exactly as typed
      return;
    }
    setReference('');
    setNote('');
  }

  return (
    <form className="space-y-3 rounded-xl border border-border bg-surface-sunk p-3" onSubmit={submit}>
      <div className="space-y-1.5">
        <label htmlFor={refId} className="text-sm font-medium text-ink">
          Add a reference
        </label>
        <p id={hintId} className="text-xs text-ink-soft">
          However you&rsquo;d say it out loud — &ldquo;Psalm 23:1-6&rdquo;, &ldquo;John 3:16&rdquo;.
        </p>
        <TextInput
          id={refId}
          aria-describedby={hintId}
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="Psalm 23:1-6"
        />
      </div>
      <div className="space-y-1.5">
        <label htmlFor={noteId} className="text-sm font-medium text-ink">
          Note <span className="font-normal text-ink-soft">(optional)</span>
        </label>
        <TextInput
          id={noteId}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Why this one"
        />
      </div>

      {error && (
        <Note tone="warn" role="alert">
          {error}
        </Note>
      )}

      <Button type="submit" variant="primary" disabled={busy || adding || !reference.trim()}>
        {adding ? 'Adding…' : 'Add to the plan'}
      </Button>
    </form>
  );
}
